import React, { useEffect, useRef, useState } from "react";
import {
  ALERT_MESSAGES,
  ALERT_TITLES,
  OnBoardingStepProps,
  ROUTES,
} from "../../../constants";
import {
  FormContainer,
  ErrorText,
  ButtonContainer,
  NextButton,
  BackButton,
  SkipButton,
  FlexContainer,
} from "../../../styles/PersonalInfoStep";
import {
  ContractText,
  SignatureCanvas,
  SignatureBox,
  ClearButton,
} from "../../../styles/SignContract";

import StyledCheckbox from "./atoms/StyledCheckbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { alertMessage } from "../../../services/alertService";
import { POST } from "../../../services/HTTPServices";
import { useNavigate } from "react-router-dom";
import {
  LoadingOverlay,
  LoadingSpinner,
  LoadingSpinnerIcon,
} from "../../../styles/loading";
import Back from "../../../assets/svgs/LoginSignup/Step1/back.svg";

const contractText = `Aliquam nec orci in sem rhoncus tempor. Quisque id metus nec tellus porttitor porttitor. Suspendisse potenti. Pellentesque fringilla porttitor tellus, id convallis sem vestibulum nec. Nullam pulvinar felis non tellus fermentum, nec dapibus arcu tincidunt. Integer nec tellus diam. Donec quis nibh ut libero hendrerit convallis.\n\nDonec consequat nunc ac turpis imperdiet, sed ultricies augue gravida. Sed tincidunt orci eu urna facilisis, a interdum odio feugiat. Vivamus sed turpis nibh. Aenean aliquam nibh id metus imperdiet dapibus. Pellentesque vitae nulla eget mauris ullamcorper finibus. Etiam cursus tincidunt nulla, non varius sapien tincidunt ac.`;

const SignContract: React.FC<OnBoardingStepProps> = ({
  formData,
  onChange,
  onNext,
  onBack,
  onAssign,
  changedRole,
  isAuthenticated
}) => {
  const navigate = useNavigate();

  const [agreed, setAgreed] = useState(false);
  const [signature, setSignature] = useState("");

  const [serviceProviderId, setServiceProviderId] = useState("");
  const [hasDrawn, setHasDrawn] = useState(false); // Track if user has drawn anything

  const [error, setError] = useState("");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawing, setDrawing] = useState(false);
  const [loading, setLoading] = React.useState(false);
  const [tempToken, setTempToken] = React.useState("");

  useEffect(() => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "null");
    if (authUser) {
      setServiceProviderId(authUser.id + "");
    } else {
      const serviceProviderId = localStorage.getItem("serviceProviderId") || "";
      if (serviceProviderId) {
        setServiceProviderId(serviceProviderId);
      }
    }

    const temp_token = localStorage.getItem("temp_token") || "";
    if (temp_token) {
      setTempToken(temp_token);
    }
  }, []);

  // Check if canvas is blank (empty)
  const isCanvasBlank = (canvas: HTMLCanvasElement): boolean => {
    const context = canvas.getContext("2d", { willReadFrequently: true });

    if (!context) return true;

    const pixelBuffer = new Uint32Array(
      context.getImageData(0, 0, canvas.width, canvas.height).data.buffer
    );

    return !pixelBuffer.some((color) => color !== 0);
  };

  // Handle drawing signature
  const handleMouseDown = (e: React.MouseEvent) => {
    setDrawing(true);
    setHasDrawn(true); // Mark that user has started drawing
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (ctx) {
        ctx.beginPath();
        ctx.moveTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!drawing) return;
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (ctx) {
        ctx.lineTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
        ctx.stroke();
      }
    }
  };

  const handleMouseUp = () => {
    setDrawing(false);
    saveSignature();
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setDrawing(true);
    setHasDrawn(true); // Mark that user has started drawing
    const canvas = canvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches[0];
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (ctx) {
        ctx.beginPath();
        ctx.moveTo(touch.clientX - rect.left, touch.clientY - rect.top);
      }
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!drawing) return;
    const canvas = canvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches[0];
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (ctx) {
        ctx.lineTo(touch.clientX - rect.left, touch.clientY - rect.top);
        ctx.stroke();
      }
    }
  };

  const handleTouchEnd = () => {
    setDrawing(false);
    saveSignature();
  };

  // Clear signature
  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
    setSignature("");
    setHasDrawn(false); // Reset the drawing state
  };

  // Save signature as dataURL only if something was drawn
  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (canvas && hasDrawn && !isCanvasBlank(canvas)) {
      setSignature(canvas.toDataURL());
    } else if (canvas && isCanvasBlank(canvas)) {
      setSignature(""); // Clear signature if canvas is blank
    }
  };

  // Form submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Check if signature exists and is not empty
    if (!signature || signature === "" || !hasDrawn) {
      setError("Please provide your signature.");
      return;
    }

    // Double-check if canvas is actually blank
    const canvas = canvasRef.current;
    if (canvas && isCanvasBlank(canvas)) {
      setError("Please provide your signature.");
      return;
    }

    if (!agreed) {
      setError("Please agree to the Terms & Conditions and Privacy Policy.");
      return;
    }

    postSignature();
  };

  const postSignature = async () => {
    setLoading(true);

    let url = "serviceprovider/SignPdfByServiceProvider";

    const base64Data = signature.split(",")[1];

    let temp = {
      serviceProviderId: serviceProviderId + "" || "",
      eSignPhotoPath: base64Data,
    };

    let res: any = await POST(url, temp, "provider");

    if (res && res.status === 200) {
      postChecklist();
    } else {
      setLoading(false);
      if (res.status && res.status === 401) navigate("/unauthorized-page");
      else {
        alertMessage(ALERT_TITLES.ERROR, res.message);
        setError(res.message);
      }
    }
  };



  const postChecklist = async () => {
    setLoading(true);
    let url = "onboardingcheck/UpdateOnboardingCheck";
    const skipKyb = localStorage.getItem("skipKyb") || "";

    let temp = {
      serviceProviderId: parseInt(serviceProviderId),
      personalInfo: 2,
      businessInfo: 2,
      kyb: skipKyb && skipKyb === "true" ? 0 : 2,
      fileUpload: 1,
      agreement: 2,
    };

    let res: any = await POST(url, temp, "provider", false, tempToken);

    if (res && res.status === 200) {
      alertMessage(ALERT_TITLES.SUCCESS, ALERT_MESSAGES.SUCCESS_SIGNUP);
      Object.keys(localStorage)
        .filter((k) => k !== "login_credentials")
        .forEach((k) => localStorage.removeItem(k));
      if (!isAuthenticated) {
        handleLogout();
      } else {
        navigate(ROUTES.PROFILE);
      }
    } else {
      setLoading(false);
      if (res.status && res.status === 401) navigate("/unauthorized-page");
      else {
        alertMessage(ALERT_TITLES.ERROR, res.message);
      }
    }
  };

  const handleLogout = async () => {
    Object.keys(localStorage)
      .filter((k) => k !== "login_credentials")
      .forEach((k) => localStorage.removeItem(k));
    navigate(ROUTES.LOGIN);
  };

  const skip = (e: React.FormEvent) => {
    e.preventDefault();

    if (!agreed) {
      setError("Please agree to the Terms & Conditions and Privacy Policy.");
      return;
    }
    alertMessage(ALERT_TITLES.SUCCESS, ALERT_MESSAGES.SUCCESS_SIGNUP);
    Object.keys(localStorage)
      .filter((k) => k !== "login_credentials")
      .forEach((k) => localStorage.removeItem(k));

    if (!isAuthenticated) {
      handleLogout();
    } else {
      navigate(ROUTES.PROFILE);
    }
  };

  return (
    <>
      {loading && (
        <LoadingOverlay>
          <LoadingSpinner>
            <LoadingSpinnerIcon />
          </LoadingSpinner>
        </LoadingOverlay>
      )}
      <FormContainer>
        <ContractText>{contractText}</ContractText>
        <SignatureBox>
          <SignatureCanvas
            ref={canvasRef}
            height={180}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          />
        </SignatureBox>
        <ClearButton type="button" onClick={clearSignature}>
          Clear Signature
        </ClearButton>
        <form onSubmit={handleSubmit}>
          <FormControlLabel
            control={
              <StyledCheckbox
                checked={agreed}
                onChange={(e : any) => setAgreed(e.target.checked)}
                inputProps={{ "aria-label": "agreement checkbox" }}
              />
            }
            label="I have read and agree to the Terms & Conditions and Privacy Policy"
          />
          {error && <ErrorText>{error}</ErrorText>}
          <ButtonContainer>
            <BackButton type="button" onClick={onBack}>
              <img src={Back} alt="Back" />
            </BackButton>
            <FlexContainer>
              <SkipButton type="button" onClick={skip}>
                Not now
              </SkipButton>
              <NextButton type="submit">Submit</NextButton>
            </FlexContainer>
          </ButtonContainer>
        </form>
      </FormContainer>
    </>
  );
};

export default SignContract;
